import React, { useState } from 'react';
import { api } from './App';
import { Wave, Calendar, Books, ArrowRight, ArrowLeft, Download, Upload, Pencil } from './Icons';
import SpreadsheetImport from './SpreadsheetImport';

const PROVIDERS = ['BARBRI', 'BPP', 'Kaplan', 'Revise SQE', 'Epimeleia', 'ULaw', 'Other'];

// SQE1 exam cohorts — dates will be confirmed by SRA; update in Settings if different
const COHORTS = [
  { id: 'jul26-both',  label: 'July 2026',      sub: 'Both FLK1 & FLK2',          flk1: '2026-07-07', flk2: '2026-07-08' },
  { id: 'jan27-both',  label: 'January 2027',   sub: 'Both FLK1 & FLK2',          flk1: '2027-01-13', flk2: '2027-01-14' },
  { id: 'jul26-flk1',  label: 'Resitting FLK1', sub: 'FLK1 only, July 2026',      flk1: '2026-07-07', flk2: '' },
  { id: 'jul26-flk2',  label: 'Resitting FLK2', sub: 'FLK2 only, July 2026',      flk1: '',           flk2: '2026-07-08' },
  { id: 'manual',      label: 'Enter manually', sub: 'I know my specific dates',   flk1: null,         flk2: null },
];

export default function Onboarding({ userName, onComplete }) {
  const [step, setStep] = useState(1);
  const [name, setName]             = useState(userName || '');
  const [flk1Date, setFlk1Date]     = useState('');
  const [flk2Date, setFlk2Date]     = useState('');
  const [selectedCohort, setSelectedCohort] = useState(null); // cohort id or null
  const [showManualDates, setShowManualDates] = useState(false);
  const [provider, setProvider]     = useState('');
  const [providerOther, setProviderOther] = useState('');
  const [saving, setSaving]         = useState(false);
  const [importCard, setImportCard] = useState(null); // null | 'template' | 'custom' | 'skip'

  // Save steps 1-3 settings then advance to step 4 (import)
  const saveAndAdvance = async (skipProvider) => {
    setSaving(true);
    const effectiveProvider = skipProvider ? '' : (provider === 'Other' ? providerOther : provider);
    const accountUpdate = { name };
    if (flk1Date) accountUpdate.exam_date_flk1 = flk1Date;
    if (flk2Date) accountUpdate.exam_date_flk2 = flk2Date;
    const settingsUpdate = {};
    if (flk1Date) settingsUpdate.flk1_exam_date = flk1Date;
    if (flk2Date) settingsUpdate.flk2_exam_date = flk2Date;
    if (effectiveProvider) settingsUpdate.material_provider = effectiveProvider;
    await Promise.all([
      api('/api/account', { method: 'PATCH', body: JSON.stringify(accountUpdate) }),
      api('/api/settings', { method: 'POST', body: JSON.stringify(settingsUpdate) }),
    ]);
    setSaving(false);
    setStep(4);
  };

  // Complete onboarding — activate tour, enter the app
  const finishOnboarding = async () => {
    await api('/api/settings', { method: 'POST', body: JSON.stringify({
      onboarding_complete: '1',
      tour_shown: '0',
    }) });
    onComplete(name, 'dashboard');
  };

  const dots = (
    <div className="ob-dots">
      {[1, 2, 3, 4].map(n => (
        <span key={n} className={`ob-dot ${step === n ? 'active' : step > n ? 'done' : ''}`} />
      ))}
    </div>
  );

  // ── Step 1: Name ──
  if (step === 1) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <div className="ob-icon"><Wave size={32} /></div>
        <h2 className="ob-title">Welcome to SQETrack</h2>
        <p className="ob-text">
          Let's take a minute to get you set up. We'll configure your exam dates, study materials, and get your dashboard working.
        </p>
        <label className="ob-label">What should we call you?</label>
        <input className="ob-input" value={name} onChange={e => setName(e.target.value)}
          placeholder="Your first name" autoFocus
          onKeyDown={e => e.key === 'Enter' && name.trim() && setStep(2)} />
        <button className="ob-btn" onClick={() => setStep(2)} disabled={!name.trim()}>
          Continue <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );

  // ── Step 2: Exam cohort ──
  if (step === 2) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <div className="ob-icon"><Calendar size={32} /></div>
        <h2 className="ob-title">Which exam sitting are you preparing for?</h2>
        <p className="ob-text">
          This powers your countdown, urgency ranking, and planner. You can update dates any time in Settings.
        </p>
        <div className="ob-cohort-grid">
          {COHORTS.map(c => (
            <button
              key={c.id}
              className={`ob-cohort-btn ${selectedCohort === c.id ? 'selected' : ''}`}
              onClick={() => {
                setSelectedCohort(c.id);
                if (c.id === 'manual') {
                  setShowManualDates(true);
                } else {
                  setShowManualDates(false);
                  if (c.flk1 !== null) setFlk1Date(c.flk1);
                  if (c.flk2 !== null) setFlk2Date(c.flk2);
                }
              }}>
              <span className="ob-cohort-label">{c.label}</span>
              <span className="ob-cohort-sub">{c.sub}</span>
            </button>
          ))}
        </div>
        {showManualDates && (
          <div className="ob-date-row" style={{ marginTop: 16 }}>
            <div className="ob-date-col">
              <label className="ob-label">FLK1 date</label>
              <input className="ob-input" type="date" value={flk1Date} onChange={e => setFlk1Date(e.target.value)} />
            </div>
            <div className="ob-date-col">
              <label className="ob-label">FLK2 date</label>
              <input className="ob-input" type="date" value={flk2Date} onChange={e => setFlk2Date(e.target.value)} />
            </div>
          </div>
        )}
        <div className="ob-btn-row" style={{ marginTop: 20 }}>
          <button className="ob-back" onClick={() => setStep(1)}><ArrowLeft size={14} /> Back</button>
          <button className="ob-btn" onClick={() => setStep(3)}>Continue <ArrowRight size={14} /></button>
        </div>
        <button className="ob-skip" onClick={() => setStep(3)}>Skip for now</button>
      </div>
    </div>
  );

  // ── Step 3: Study materials ──
  if (step === 3) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <div className="ob-icon"><Books size={32} /></div>
        <h2 className="ob-title">Which study materials are you using?</h2>
        <p className="ob-text">
          This helps us give the most relevant guidance when importing MCQ results.
        </p>
        <div className="ob-provider-grid">
          {PROVIDERS.map(p => (
            <button key={p} className={`ob-provider-btn ${provider === p ? 'selected' : ''}`}
              onClick={() => setProvider(p)}>{p}</button>
          ))}
        </div>
        {provider === 'Other' && (
          <input className="ob-input" value={providerOther} onChange={e => setProviderOther(e.target.value)}
            placeholder="Which provider?" style={{ marginTop: 10 }} autoFocus />
        )}
        <div className="ob-btn-row">
          <button className="ob-back" onClick={() => setStep(2)}><ArrowLeft size={14} /> Back</button>
          <button className="ob-btn" onClick={() => saveAndAdvance(false)} disabled={saving}>
            {saving ? 'Setting up…' : <>Continue <ArrowRight size={14} /></>}
          </button>
        </div>
        <button className="ob-skip" onClick={() => saveAndAdvance(true)}>Skip for now</button>
      </div>
    </div>
  );

  // ── Step 4: Import data ──
  if (step === 4) {
    // Sub-flow: user picked template or custom and is working through it
    if (importCard === 'template' || importCard === 'custom') {
      return (
        <div className="ob-fullpage">
          <div className="ob-fullpage-inner">
            {dots}
            <SpreadsheetImport
              onBack={() => setImportCard(null)}
              onDone={finishOnboarding}
            />
            <button className="ob-skip ob-skip--onlight" onClick={finishOnboarding}>Skip import and continue</button>
          </div>
        </div>
      );
    }

    return (
      <div className="ob-fullpage">
        <div className="ob-fullpage-inner">
          {dots}
          <h2 className="ob-title ob-title--left">Get your dashboard working in minutes</h2>
          <p className="ob-text ob-text--left">
            Import your existing confidence scores, or rate topics as you go. The more data you have, the sharper your priorities.
          </p>

          <div className="ob-import-cards-v2">
            <button className="ob-import-card-v2" onClick={() => setImportCard('template')}>
              <div className="ob-import-card-v2-icon ob-import-card-v2-icon--blue">
                <Download size={20} />
              </div>
              <div className="ob-import-card-v2-body">
                <div className="ob-import-card-v2-title">Download our template</div>
                <div className="ob-import-card-v2-text">Get a spreadsheet with all 259 topics pre-filled. Rate your confidence, upload it back.</div>
              </div>
              <ArrowRight size={16} className="ob-import-card-v2-arrow" />
            </button>

            <button className="ob-import-card-v2" onClick={() => setImportCard('custom')}>
              <div className="ob-import-card-v2-icon ob-import-card-v2-icon--blue">
                <Upload size={20} />
              </div>
              <div className="ob-import-card-v2-body">
                <div className="ob-import-card-v2-title">Import your own spreadsheet</div>
                <div className="ob-import-card-v2-text">Already tracking topics? Upload your spreadsheet and we'll match your scores automatically.</div>
              </div>
              <ArrowRight size={16} className="ob-import-card-v2-arrow" />
            </button>

            <button className="ob-import-card-v2 ob-import-card-v2--skip" onClick={finishOnboarding}>
              <div className="ob-import-card-v2-icon ob-import-card-v2-icon--grey">
                <Pencil size={20} />
              </div>
              <div className="ob-import-card-v2-body">
                <div className="ob-import-card-v2-title">Start with a blank slate</div>
                <div className="ob-import-card-v2-text">Rate topics directly in the app as you revise. No import needed.</div>
              </div>
              <ArrowRight size={16} className="ob-import-card-v2-arrow ob-import-card-v2-arrow--grey" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
